import React, { useState, useEffect } from 'react';
import api from '../Services/api';
import * as XLSX from 'xlsx';

const Calls = () => {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Summary Stats
    const [stats, setStats] = useState({
        total_calls: 0,
        connected_calls: 0,
        missed_calls: 0,
        total_duration_sec: 0
    });

    // Filter State
    const [search, setSearch] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [callType, setCallType] = useState('');
    const [employeeId, setEmployeeId] = useState('');
    const [statusFilter, setStatusFilter] = useState('');

    // Pagination State
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);

    // Dynamic Filter Options
    const [filterOptions, setFilterOptions] = useState({
        call_types: [],
        employees: [],
        crm_statuses: [],
        derived_statuses: []
    });

    const fetchFilterOptions = async () => {
        try {
            const response = await api.get('calls/filters/');
            setFilterOptions(response.data);
        } catch (error) {
            console.error("Error fetching filter options:", error);
        }
    };

    const fetchLogs = async (page = 1, showLoading = true) => {
        if (showLoading) setLoading(true);
        try {
            const params = {
                page: page,
                search: search,
                start_date: startDate,
                end_date: endDate,
                call_type: callType,
                employee_id: employeeId,
                status: statusFilter
            };

            const response = await api.get('calls/list/', { params });
            
            // Handle Paginated Response
            if (response.data.results) {
                setLogs(response.data.results);
                setTotalCount(response.data.count);
                // DRF doesn't always give total pages, calculate if missing
                const calculatedTotal = Math.ceil(response.data.count / 20); 
                setTotalPages(calculatedTotal);
                
                if (response.data.stats) {
                    setStats(response.data.stats);
                }
            } else {
                // Fallback for non-paginated
                setLogs(response.data.results || response.data);
                if (response.data.stats) setStats(response.data.stats);
            }
            
            setError(null);
            setLoading(false);
        } catch (error) {
            console.error("Error fetching logs:", error);
            setError(error.response?.data?.detail || error.message);
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchFilterOptions();
    }, []);

    useEffect(() => {
        fetchLogs(currentPage, true);
    }, [currentPage, startDate, endDate, callType, employeeId, statusFilter]);

    useEffect(() => {
        const timer = setTimeout(() => {
            if (currentPage !== 1) setCurrentPage(1);
            else fetchLogs(1, true);
        }, 500);
        return () => clearTimeout(timer);
    }, [search]);

    const getDetailedStatus = (log) => {
        if (log.duration > 0) return "Connected";
        if (log.call_type === "Incoming") return "Missed/Rejected";
        return "Not Connected";
    };

    const formatDuration = (sec) => {
        if (!sec) return "0s";
        const h = Math.floor(sec / 3600);
        const m = Math.floor((sec % 3600) / 60);
        const s = sec % 60;
        return h > 0 ? `${h}h ${m}m ${s}s` : m > 0 ? `${m}m ${s}s` : `${s}s`;
    };

    const handleExport = async () => {
        try {
            const params = {
                search: search,
                start_date: startDate,
                end_date: endDate,
                call_type: callType,
                employee_id: employeeId,
                status: statusFilter
            };

            const response = await api.get('calls/export/', {
                params,
                responseType: 'blob',
            });

            // Read the CSV data from the backend
            const reader = new FileReader();
            reader.onload = (e) => {
                const csvContent = e.target.result;
                const workbook = XLSX.read(csvContent, { type: 'string' });
                XLSX.writeFile(workbook, `call_logs_${new Date().toISOString().split('T')[0]}.xlsx`);
            };
            reader.readAsText(response.data);
            
        } catch (error) {
            console.error("Error exporting logs:", error);
            alert("Failed to export logs. Please try again.");
        }
    };

    return (
        <div style={{ padding: '2rem', backgroundColor: '#f8fafc', minHeight: '100vh' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
                <div>
                    <h2 style={{ margin: 0, color: '#1e293b', fontSize: '1.875rem', fontWeight: '700' }}>Telephony Logs</h2>
                    <p style={{ margin: '0.25rem 0 0 0', color: '#64748b' }}>Real-time synchronization with Callyzer</p>
                </div>
                <div style={{ display: 'flex', gap: '1rem' }}>
                    <button 
                        onClick={handleExport}
                        style={{ 
                            padding: '0.625rem 1.25rem', 
                            backgroundColor: '#fff', 
                            color: '#475569', 
                            border: '1px solid #e2e8f0', 
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontWeight: '600',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)'
                        }}
                    >
                        📊 Download Excel Report
                    </button>
                    <button 
                        onClick={() => fetchLogs(currentPage, true)}
                        disabled={loading}
                        style={{ 
                            padding: '0.625rem 1.25rem', 
                            backgroundColor: loading ? '#94a3b8' : '#799F0C', 
                            color: 'white', 
                            border: 'none', 
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontWeight: '600',
                            boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
                        }}
                    >
                        {loading ? "Syncing..." : "Refresh Logs"}
                    </button>
                </div>
            </div>

            {/* KPI Summary Cards */}
            <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                gap: '1.5rem', 
                marginBottom: '2rem' 
            }}>
                {[
                    { label: 'Total Calls', value: stats.total_calls, color: '#6366f1', bg: '#eef2ff' },
                    { label: 'Connected', value: stats.connected_calls, color: '#059669', bg: '#ecfdf5' },
                    { label: 'Missed/Rejected', value: stats.missed_calls, color: '#dc2626', bg: '#fef2f2' },
                    { label: 'Total Duration', value: formatDuration(stats.total_duration_sec), color: '#d97706', bg: '#fffbeb' },
                ].map((kpi, i) => (
                    <div key={i} style={{ 
                        backgroundColor: 'white', 
                        padding: '1.5rem', 
                        borderRadius: '16px', 
                        boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                        borderLeft: `6px solid ${kpi.color}`
                    }}>
                        <div style={{ fontSize: '0.875rem', fontWeight: '600', color: '#64748b', marginBottom: '0.5rem' }}>{kpi.label}</div>
                        <div style={{ fontSize: '1.75rem', fontWeight: '800', color: '#1e293b' }}>{kpi.value}</div>
                        <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginTop: '0.25rem' }}>Based on active filters</div>
                    </div>
                ))}
            </div>

            {/* Filter Bar */}
            <div style={{ 
                backgroundColor: 'white', 
                padding: '1.5rem', 
                borderRadius: '12px', 
                boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)',
                marginBottom: '1.5rem',
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '1rem',
                alignItems: 'end'
            }}>
                <div>
                    <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '600', color: '#64748b', marginBottom: '0.5rem' }}>SEARCH</label>
                    <input 
                        type="text" 
                        placeholder="Client name, number..." 
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #e2e8f0', borderRadius: '6px', outline: 'none' }}
                    />
                </div>
                <div>
                    <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '600', color: '#64748b', marginBottom: '0.5rem' }}>FROM DATE</label>
                    <input 
                        type="date" 
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #e2e8f0', borderRadius: '6px', outline: 'none' }}
                    />
                </div>
                <div>
                    <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '600', color: '#64748b', marginBottom: '0.5rem' }}>TO DATE</label>
                    <input 
                        type="date" 
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #e2e8f0', borderRadius: '6px', outline: 'none' }}
                    />
                </div>
                <div>
                    <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '600', color: '#64748b', marginBottom: '0.5rem' }}>STATUS</label>
                    <select 
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #e2e8f0', borderRadius: '6px', outline: 'none', backgroundColor: 'white' }}
                    >
                        <option value="">All Status</option>
                        {filterOptions.derived_statuses.map(s => <option key={s} value={s}>{s}</option>)}
                        {filterOptions.crm_statuses.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                </div>
                <div>
                    <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '600', color: '#64748b', marginBottom: '0.5rem' }}>EMPLOYEE</label>
                    <select 
                        value={employeeId}
                        onChange={(e) => setEmployeeId(e.target.value)}
                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #e2e8f0', borderRadius: '6px', outline: 'none', backgroundColor: 'white' }}
                    >
                        <option value="">All Employees</option>
                        {filterOptions.employees.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                    </select>
                </div>
                <div>
                    <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: '600', color: '#64748b', marginBottom: '0.5rem' }}>CALL TYPE</label>
                    <select 
                        value={callType}
                        onChange={(e) => setCallType(e.target.value)}
                        style={{ width: '100%', padding: '0.5rem', border: '1px solid #e2e8f0', borderRadius: '6px', outline: 'none', backgroundColor: 'white' }}
                    >
                        <option value="">All Types</option>
                        {filterOptions.call_types.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                </div>
                <button 
                    onClick={() => {
                        setSearch('');
                        setStartDate('');
                        setEndDate('');
                        setCallType('');
                        setEmployeeId('');
                        setStatusFilter('');
                        setCurrentPage(1);
                    }}
                    style={{ padding: '0.5rem', backgroundColor: '#f1f5f9', color: '#475569', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.75rem', fontWeight: '600' }}
                >
                    Reset Filters
                </button>
            </div>

            {error && (
                <div style={{ padding: '1rem', backgroundColor: '#fee2e2', color: '#dc2626', borderRadius: '8px', marginBottom: '1rem', fontSize: '0.875rem' }}>
                    <strong>Access Denied or Error:</strong> {error}
                </div>
            )}

            <div style={{ backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)', overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                    <thead>
                        <tr style={{ backgroundColor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                            <th style={{ padding: '1rem', fontSize: '0.75rem', fontWeight: '700', color: '#64748b', textTransform: 'uppercase' }}>Call Time</th>
                            <th style={{ padding: '1rem', fontSize: '0.75rem', fontWeight: '700', color: '#64748b', textTransform: 'uppercase' }}>Employee</th>
                            <th style={{ padding: '1rem', fontSize: '0.75rem', fontWeight: '700', color: '#64748b', textTransform: 'uppercase' }}>Client</th>
                            <th style={{ padding: '1rem', fontSize: '0.75rem', fontWeight: '700', color: '#64748b', textTransform: 'uppercase' }}>Direction</th>
                            <th style={{ padding: '1rem', fontSize: '0.75rem', fontWeight: '700', color: '#64748b', textTransform: 'uppercase' }}>Status</th>
                            <th style={{ padding: '1rem', fontSize: '0.75rem', fontWeight: '700', color: '#64748b', textTransform: 'uppercase' }}>Duration</th>
                            <th style={{ padding: '1rem', fontSize: '0.75rem', fontWeight: '700', color: '#64748b', textTransform: 'uppercase' }}>Recording</th>
                        </tr>
                    </thead>
                    <tbody>
                        {logs.length > 0 ? logs.map((log) => {
                            const status = getDetailedStatus(log);
                            const displayName = log.user_details ? 
                                `${log.user_details.first_name} ${log.user_details.last_name}` : 
                                (log.emp_name || 'Unknown');

                            return (
                                <tr key={log.id} style={{ borderBottom: '1px solid #f1f5f9', transition: 'background-color 0.2s' }}>
                                    <td style={{ padding: '1rem' }}>
                                        <div style={{ fontSize: '0.875rem', color: '#1e293b' }}>{new Date(log.call_time).toLocaleDateString()}</div>
                                        <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{new Date(log.call_time).toLocaleTimeString()}</div>
                                    </td>
                                    <td style={{ padding: '1rem' }}>
                                        <div style={{ fontWeight: '600', color: '#1e293b', fontSize: '0.875rem' }}>
                                            {displayName}
                                            {log.user_details && <span style={{ marginLeft: '0.5rem', fontSize: '0.65rem', color: '#6366f1', backgroundColor: '#eef2ff', padding: '0.1rem 0.4rem', borderRadius: '4px', border: '1px solid #e0e7ff' }}>CRM</span>}
                                        </div>
                                        <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{log.emp_number}</div>
                                    </td>
                                    <td style={{ padding: '1rem' }}>
                                        <div style={{ fontWeight: '600', color: '#1e293b', fontSize: '0.875rem' }}>{log.client_name || 'Unknown'}</div>
                                        <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{log.client_number}</div>
                                    </td>
                                    <td style={{ padding: '1rem' }}>
                                        <span style={{ 
                                            padding: '0.25rem 0.625rem', 
                                            borderRadius: '9999px', 
                                            fontSize: '0.7rem', 
                                            fontWeight: '600',
                                            backgroundColor: log.call_type === 'Incoming' ? '#ecfdf5' : '#eef2ff',
                                            color: log.call_type === 'Incoming' ? '#059669' : '#4f46e5'
                                        }}>
                                            {log.call_type}
                                        </span>
                                    </td>
                                    <td style={{ padding: '1rem' }}>
                                        <span style={{ 
                                            color: status === "Connected" ? "#059669" : "#dc2626",
                                            fontWeight: '600',
                                            fontSize: '0.875rem'
                                        }}>
                                            {status}
                                        </span>
                                    </td>
                                    <td style={{ padding: '1rem', fontSize: '0.875rem', color: '#1e293b' }}>
                                        {log.duration > 0 ? (
                                            log.duration > 60 
                                                ? `${Math.floor(log.duration / 60)}m ${log.duration % 60}s` 
                                                : `${log.duration}s`
                                        ) : "--"}
                                    </td>
                                    <td style={{ padding: '1rem' }}>
                                        {log.call_recording_url ? (
                                            <a 
                                                href={log.call_recording_url} 
                                                target="_blank" 
                                                rel="noopener noreferrer"
                                                style={{ color: '#6366f1', textDecoration: 'none', fontWeight: '600', fontSize: '0.875rem' }}
                                            >
                                                Listen
                                            </a>
                                        ) : (
                                            <span style={{ color: '#94a3b8', fontSize: '0.875rem' }}>No Audio</span>
                                        )}
                                    </td>
                                </tr>
                            );
                        }) : (
                            <tr>
                                <td colSpan="7" style={{ padding: '3rem', textAlign: 'center', color: '#64748b' }}>
                                    {loading ? "Searching through logs..." : "No call logs match your current filters."}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>

                {/* Pagination Controls */}
                <div style={{ 
                    padding: '1rem 1.5rem', 
                    borderTop: '1px solid #e2e8f0', 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    backgroundColor: '#f8fafc'
                }}>
                    <div style={{ fontSize: '0.875rem', color: '#64748b' }}>
                        Showing page <span style={{ fontWeight: '600', color: '#1e293b' }}>{currentPage}</span> of <span style={{ fontWeight: '600', color: '#1e293b' }}>{totalPages || 1}</span>
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button 
                            disabled={currentPage === 1 || loading}
                            onClick={() => setCurrentPage(prev => prev - 1)}
                            style={{ 
                                padding: '0.5rem 1rem', 
                                border: '1px solid #e2e8f0', 
                                borderRadius: '6px', 
                                backgroundColor: 'white',
                                color: currentPage === 1 ? '#cbd5e1' : '#475569',
                                cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                                fontSize: '0.875rem',
                                fontWeight: '500'
                            }}
                        >
                            Previous
                        </button>
                        <button 
                            disabled={currentPage === totalPages || totalPages === 0 || loading}
                            onClick={() => setCurrentPage(prev => prev + 1)}
                            style={{ 
                                padding: '0.5rem 1rem', 
                                border: '1px solid #e2e8f0', 
                                borderRadius: '6px', 
                                backgroundColor: 'white',
                                color: (currentPage === totalPages || totalPages === 0) ? '#cbd5e1' : '#475569',
                                cursor: (currentPage === totalPages || totalPages === 0) ? 'not-allowed' : 'pointer',
                                fontSize: '0.875rem',
                                fontWeight: '500'
                            }}
                        >
                            Next
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Calls;
